
import { useCallback, useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import httpClient from '../httpClient'

import './LabOrders.css'

import { apiErrorMessage ,getCookie} from '../lib/utils'

import LabOrdersHeader from '../components/LabOrders/LabOrdersHeader'
import LabOrders from '../components/LabOrders'
import Loading from '../components/common/Loading'
import ErrorMessage from '../components/common/ErrorMessage'
import PhotographersPagination from '../components/PhotographersDatabase/PhotographersPagination'
import { useLabOrderDetailsStore } from '../stores'


const ORDERS_PER_PAGE = 200

const LabOrdersPage = () => {
  const location = useLocation()
  
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setApiError] = useState(null)
  const [orders, setOrders] = useState(null)
  const [metadata, setMetadata] = useState(null)
  const [pagination, setPagination] = useState({ search: location.search, page: 1 })
  const requestId = useRef(0)
  const page = pagination.search === location.search ? pagination.page : 1

  const latestOrder = useLabOrderDetailsStore((state) => state.order?.data)
  const latestOrderProgress = useLabOrderDetailsStore((state) => state.progressPercent)

   const isAdmin = getCookie('isUserAdmin') === 'true'

  const searchOrders = useCallback (async () =>{
    const currentRequestId = ++requestId.current
    try {
      setLoading('Loading orders')
      setApiError(null)
      const endpoint = `lab_orders/search${location.search ?? ''}`
      const { data, meta } = await httpClient(endpoint, {
        params: { page, per_page: ORDERS_PER_PAGE },
      })
      if (currentRequestId !== requestId.current) return
      const lastPage = Math.max(1, Number(meta?.totalPages) || 1)
      if (page > lastPage) {
        setPagination({ search: location.search, page: lastPage })
        return
      }
      setOrders(data)
      setMetadata(meta)
      setLoading(false)
    } catch (err) {
      if (currentRequestId !== requestId.current) return
      setApiError(apiErrorMessage(err))
      setLoading(false)
    }
  },[location.search, page])

  useEffect(()=>{
    if (!location.search) return undefined
    // Let Strict Mode cleanup settle before fetching.
    const timeout = window.setTimeout(searchOrders, 0)
    return () => {
      window.clearTimeout(timeout)
      requestId.current += 1
    }
  },[searchOrders, location.search, isAdmin])

  useEffect(() => {
    setPagination({ search: location.search, page: 1 })
  }, [location.search, isAdmin])

  const handleDeleteOrders = useCallback(async (id) => {
    try {
      setDeleting(true)
      await httpClient.delete(`lab_orders/${id}`)
      await searchOrders()
      setDeleting(false)
    } catch (err) {
      setApiError(apiErrorMessage(err))
      setDeleting(false)
    }
  }, [searchOrders])

  const handleUpdateOrder = useCallback(async (id, type, value) => {
    try {
      const response = await httpClient.patch(`lab_orders/${id}`, { type, value })
      setOrders((prevOrders) => {
        if (!prevOrders) return prevOrders
        
        return prevOrders.map((order) => {
          const isMatch = order.attributes?.guid === id || order.id === id
          
          if (isMatch) {
            return response.data
          }
          
          return order
        })
      })
    } catch (err) {
      setApiError(apiErrorMessage(err))
    }
  }, [])

  // useEffect(() => {
  //   searchOrders()
  // }, [location.search])

  if (deleting) {
    return <Loading message="Deleting order" />
  }

  if (error) {
    return <ErrorMessage errors={error} />
  }
  const ordersWithLatestProgress = orders?.map((order) => {
    if (order.attributes?.guid !== latestOrder?.attributes?.guid) return order

    return {
      ...order,
      attributes: {
        ...order.attributes,
        progress: latestOrderProgress,
      },
    }
  })
  const totalPages = Math.max(1, Number(metadata?.totalPages) || 1)

  return (
    <div className={`w-layout-grid grid-main-container lab-orders-paginated ${isAdmin ? 'lab-orders-admin' : ''}`}>
      <LabOrdersHeader
        key={!location.search ? 'default-statuses' : undefined}
        isAdmin={isAdmin}
        location={location}
      />
      {loading ? (
        <Loading message="Loading orders" />
      ) : (
        <LabOrders
          key={page}
          orders={ordersWithLatestProgress}
          onDelete={handleDeleteOrders}
          onUpdateOrder={handleUpdateOrder}
          isAdmin={isAdmin}
        />
      )}
      {!loading && (
        <PhotographersPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(nextPage) => setPagination({ search: location.search, page: nextPage })}
          ariaLabel="Orders pagination"
          className="lab-orders-pagination"
        />
      )}
    </div>
  )
}

export default LabOrdersPage
